import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import './OtpInput.css';

/**
 * QHS OtpInput — segmented one-time-code input.
 *
 * Renders `length` independent boxes that together hold a single
 * value string. Auto-advances to the next box as digits are typed,
 * supports backspace to step back, arrow-key navigation, and paste
 * (paste a full code anywhere — it spreads across the boxes).
 *
 * The component is fully controlled — pass `value` (string of digits,
 * possibly shorter than `length`) and an `onChange(nextValue)` that
 * receives the merged string. When the user fills the last box, the
 * `onComplete(value)` callback fires once.
 *
 * Props:
 *   - value:      controlled string ('' to length characters)
 *   - onChange:   fired whenever the value changes
 *   - onComplete: fired once when the last box is filled
 *   - length:     number of boxes (default 6)
 *   - inputType:  'numeric' (default) | 'alphanumeric'
 *   - autoFocus:  focus the first empty box on mount
 *   - error:      adds error styling to all boxes
 *   - disabled:   disable all boxes
 *   - id:         passed to the first input for label association
 */
const NUMERIC_RE = /^\d$/;
const ALPHANUM_RE = /^[a-zA-Z0-9]$/;

const OtpInput = forwardRef(function OtpInput(
  {
    value = '',
    onChange,
    onComplete,
    length = 6,
    inputType = 'numeric',
    autoFocus = false,
    error = false,
    disabled = false,
    id,
    className,
    ...rest
  },
  ref
) {
  const refs = useRef([]);
  const charRe = inputType === 'alphanumeric' ? ALPHANUM_RE : NUMERIC_RE;
  const completedRef = useRef(false);

  const chars = useMemo(() => {
    const arr = Array.from({ length }, (_, i) => value[i] || '');
    return arr;
  }, [value, length]);

  // Fire onComplete once when the value reaches full length.
  useEffect(() => {
    if (value.length === length && !completedRef.current) {
      completedRef.current = true;
      if (onComplete) onComplete(value);
    } else if (value.length < length) {
      completedRef.current = false;
    }
  }, [value, length, onComplete]);

  useEffect(() => {
    if (autoFocus) {
      const firstEmpty = chars.findIndex(c => !c);
      const focusIndex = firstEmpty === -1 ? length - 1 : firstEmpty;
      refs.current[focusIndex]?.focus();
    }
    // intentionally only on mount
  }, []); // eslint-disable-line

  const updateValue = useCallback(
    nextChars => {
      const merged = nextChars.join('').slice(0, length);
      if (onChange) onChange(merged);
    },
    [length, onChange]
  );

  const handleInput = (i, e) => {
    const raw = e.target.value;
    // The input is maxLength=1, but Android IMEs sometimes emit the
    // full composed string — take the last typed char that matches.
    const lastChar = raw[raw.length - 1] || '';
    if (lastChar && !charRe.test(lastChar)) {
      // Force re-render to clear out invalid input.
      e.target.value = chars[i] || '';
      return;
    }
    const next = [...chars];
    next[i] = lastChar.toUpperCase();
    updateValue(next);
    if (lastChar && i < length - 1) {
      refs.current[i + 1]?.focus();
      refs.current[i + 1]?.select?.();
    }
  };

  const handleKeyDown = (i, e) => {
    const key = e.key;
    if (key === 'Backspace') {
      if (chars[i]) {
        // Backspace clears the current box (no jump).
        const next = [...chars];
        next[i] = '';
        updateValue(next);
      } else if (i > 0) {
        // Backspace on empty box → jump back and clear.
        e.preventDefault();
        const next = [...chars];
        next[i - 1] = '';
        updateValue(next);
        refs.current[i - 1]?.focus();
        refs.current[i - 1]?.select?.();
      }
    } else if (key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
      refs.current[i - 1]?.select?.();
    } else if (key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
      refs.current[i + 1]?.select?.();
    } else if (key === 'Home') {
      e.preventDefault();
      refs.current[0]?.focus();
    } else if (key === 'End') {
      e.preventDefault();
      refs.current[length - 1]?.focus();
    }
  };

  const handlePaste = (i, e) => {
    e.preventDefault();
    const text = (e.clipboardData?.getData('text') || '').trim();
    const filtered = text
      .split('')
      .filter(c => charRe.test(c))
      .slice(0, length - i);
    if (!filtered.length) return;
    const next = [...chars];
    for (let k = 0; k < filtered.length; k += 1) {
      next[i + k] = filtered[k].toUpperCase();
    }
    updateValue(next);
    const focusIndex = Math.min(i + filtered.length, length - 1);
    refs.current[focusIndex]?.focus();
    refs.current[focusIndex]?.select?.();
  };

  return (
    <div
      ref={ref}
      className={cx(
        'qhs-otp',
        error && 'qhs-otp--error',
        disabled && 'qhs-otp--disabled',
        className
      )}
      role="group"
      aria-label="One-time code"
      {...rest}
    >
      {chars.map((char, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode={inputType === 'numeric' ? 'numeric' : 'text'}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          className="qhs-otp__cell"
          aria-label={`Digit ${i + 1} of ${length}`}
          value={char}
          disabled={disabled}
          onChange={e => handleInput(i, e)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={e => handlePaste(i, e)}
          onFocus={e => e.target.select?.()}
          id={i === 0 ? id : undefined}
        />
      ))}
    </div>
  );
});

export default OtpInput;
